import { Map } from "immutable";
import { Application } from "@domain/application/application";
import { ApplicationDto, applicationFromDto, applicationToDto } from "@domain/application/dtos";
import { InitializationState } from "@domain/currentProfile/initializationState";
import { GroupDto, groupFromDto, groupToDto } from "@domain/group/dtos";
import { Group } from "@domain/group/group";
import { Article } from "@domain/lms/article/article";
import { ArticleDto, articleFromDto, articleToDto } from "@domain/lms/article/dtos";
import { Course } from "@domain/lms/course/course";
import { CourseDto, courseFromDto, courseToDto } from "@domain/lms/course/dtos";
import { OrganizationDto, organizationFromDto, organizationToDto } from "@domain/organization/dtos";
import { Organization } from "@domain/organization/organization";
import { ProfileDto, profileFromDto, profileToDto } from "@domain/user/dtos";
import { Profile } from "@domain/user/profile";
import { WalletDto, walletFromDto, walletToDto } from "@domain/wallet/dto";
import { Wallet } from "@domain/wallet/wallet";

export interface AppInitialState {
	initializationState: InitializationState;
	profiles: Map<string, Profile>;
	organizations: Map<string, Organization>;
	groups: Map<string, Group>;
	courses: Map<string, Course>;
	articles: Map<string, Article>;
	application?: Map<string, Application>;
	wallet?: Map<string, Wallet>;
}

export interface AppInitialStateDto extends Omit<
	AppInitialState,
	"profiles" | "organizations" | "groups" | "courses" | "articles" | "application" | "wallet"
> {
	profiles: [string, ProfileDto][];
	organizations: [string, OrganizationDto][];
	groups: [string, GroupDto][];
	courses: [string, CourseDto][];
	articles: [string, ArticleDto][];
	application?: [string, ApplicationDto][];
	wallet?: [string, WalletDto][];
}

export function appInitialStateToDto(initialState: AppInitialState): AppInitialStateDto {
	return {
		...initialState,
		profiles: initialState.profiles.toArray().map(([k, profile]) => [k, profileToDto(profile)]),
		organizations: initialState.organizations
			.toArray()
			.map(([k, organization]) => [k, organizationToDto(organization)]),
		groups: initialState.groups.toArray().map(([k, group]) => [k, groupToDto(group)]),
		courses: initialState.courses.toArray().map(([k, course]) => [k, courseToDto(course)]),
		articles: initialState.articles.toArray().map(([k, article]) => [k, articleToDto(article)]),
		application: initialState.application?.toArray()?.map(([k, application]) => [k, applicationToDto(application)]),
		wallet: initialState.wallet?.toArray()?.map(([k, wallet]) => [k, walletToDto(wallet)]),
	};
}

export function appInitialStateFromDto(initialState: AppInitialStateDto): AppInitialState {
	return {
		...initialState,
		profiles: Map(initialState.profiles.map(([k, profile]) => [k, profileFromDto(profile)])),
		organizations: Map(initialState.organizations.map(([k, organization]) => [k, organizationFromDto(organization)])),
		groups: Map(initialState.groups.map(([k, group]) => [k, groupFromDto(group)])),
		courses: Map(initialState.courses.map(([k, course]) => [k, courseFromDto(course)])),
		articles: Map(initialState.articles.map(([k, article]) => [k, articleFromDto(article)])),
		application: Map(initialState.application?.map(([k, application]) => [k, applicationFromDto(application)])),
		wallet: Map(initialState.wallet?.map(([k, wallet]) => [k, walletFromDto(wallet)])),
	};
}
